import { gql } from '@apollo/client';
import React from 'react';

import { Trans, useTranslation } from 'react-i18next';
import Avatar from '@justgiving/avatar';

import { StickySummaryFragment } from '../../../types/__generated__';
import {
  buildQuery,
  MessageOverrides,
  useBasePageQuery,
  usePageQuery,
} from '../../hooks';
import { filterByTypename } from '../../utils/graphql';
import { makePhotoUrlFromAbsoluteUrl } from '../../utils/makePhotoUrl';
import DonateButton from '../DonateButton/DonateButton';
import PageSummary from '../PageSummary/PageSummary';

import styles from './StickySummary.scss';
import { Share } from '../SocialButtons/Share';
import { LOGO } from '../../constants/imageTemplateStrings';

const FRAGMENT = buildQuery(gql`
  fragment StickySummary on Page {
    owner {
      id
      avatar
    }
    relationships {
      campaigns(first: 1) {
        nodes {
          id
          title
        }
        totalCount
      }
    }
  }
`);

interface StickySummaryProps {
  shareComponent?: JSX.Element;
  shareMessageOverrides?: MessageOverrides;
  donateButtonExperiments?: string[];
}

const StickySummary: React.FunctionComponent<StickySummaryProps> = ({
  shareComponent,
  shareMessageOverrides,
  donateButtonExperiments,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const result = usePageQuery<StickySummaryFragment>(FRAGMENT);
  const basePage = useBasePageQuery();

  const basePageData = basePage.data?.page;
  const name = basePageData?.owner?.name ?? basePageData?.title ?? '';
  const beneficiaries = basePageData?.relationships?.beneficiaries;
  const charityName = beneficiaries?.nodes
    ? filterByTypename(beneficiaries.nodes, 'Charity')[0]?.name
    : null;

  const owner = result.data?.owner;
  const relationships = result.data?.relationships;

  function getAvatarImage() {
    if (owner?.avatar) {
      return (
        <Avatar
          picture={makePhotoUrlFromAbsoluteUrl(owner.avatar)}
          avatarSize="auto"
          hasBorder
        />
      );
    }

    const pageLogo = basePageData?.logo || basePageData?.branding?.logo;
    if (pageLogo) {
      return (
        <div className={`jg-bd jg-bd--very-light-grey ${styles.pageLogo}`}>
          <img
            src={makePhotoUrlFromAbsoluteUrl(pageLogo, LOGO)}
            alt=""
            data-qa="stickySummary-pageLogo"
          />
        </div>
      );
    }

    return undefined;
  }

  const campaignName = relationships?.campaigns.nodes[0]
    ? relationships.campaigns.nodes[0].title
    : null;

  let subtitle;
  if (charityName && campaignName) {
    subtitle = (
      <Trans i18nKey="fundraising.subtitleCampaign">
        <span>Fundraising for </span>
        <span className="jg-text--regular">{{ campaignName }}</span>
        <span> run by </span>
        <span className="jg-text--regular">{{ charityName }}</span>
      </Trans>
    );
  } else if (charityName) {
    subtitle = (
      <>
        {t('fundraising.fundraisingFor', 'Fundraising for')}
        <span className="jg-text--regular"> {charityName}</span>
      </>
    );
  }

  return (
    <PageSummary
      image={getAvatarImage()}
      pagetitle={name}
      pagesubtitle={subtitle}
      ctas={
        <div className="row row--no-gutter jg-w-full">
          <div className="col-sm-6 jg-space-prxs">
            <DonateButton
              key="donateButton"
              trackingDataPageSection="StickySummary"
              experiments={donateButtonExperiments}
              successType="StickyDonateButtonClick"
            />
          </div>
          <div className="col-sm-6 jg-space-plxs jg-space-pvsm">
            {shareComponent ?? (
              <Share
                successType="StickyShareButtonClick"
                messageOverrides={shareMessageOverrides}
              />
            )}
          </div>
        </div>
      }
    />
  );
};

StickySummary.displayName = 'StickySummary';

export default StickySummary;
